<template>
    <li v-if="tab.condition === undefined || tab.condition" class="list_item">
        <NuxtLink v-slot="{navigate, isActive, href}" :to="toUrl" custom>
            <a
                :data-testid="`tablink-${tab.name}`"
                class="list_item_link"
                :class="[isActive && 'router-link-active']"
                :href="href"
                :title="tab.title"
                @click="navigate"
            >
                <span class="list_item_link_icon">
                    <VJoyIcon :name="iconName" :color="isActive ? undefined : 'neutral'" />
                </span>
                <span class="list_item_link_label">
                    {{ tab.title }}
                </span>

                <span v-if="notificationLabel" class="list_item_link_notification">
                    <VJoyBadge :label="notificationLabel" :bulletpoint="false" variant="red" />
                </span>
                <span v-else-if="isBrandNew" class="list_item_link_new">
                    <VJoyTag variant="special" size="small" :label="t('client.settings.tab.new')" />
                </span>
                <span v-if="isInvalid" class="list_item_link_status">
                    <VJoyIcon name="warning" color="error" />
                </span>
            </a>
        </NuxtLink>
    </li>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {TabItem} from '@common/types';
    import {useGetCompanyId} from '@common/utils';
    import {useTranslation} from '#imports';
    import type {TJoyIconsNames} from '@maltjoy/core-vue';
    import {VJoyIcon, VJoyBadge, VJoyTag} from '@maltjoy/core-vue';

    const {t} = useTranslation();

    const props = defineProps<{
        tab: TabItem;
    }>();

    const iconName = ref<TJoyIconsNames>(props.tab.icon as TJoyIconsNames);
    const isInvalid = computed(() => props.tab.state === 'INVALID');
    const isBrandNew = computed(() => !!props.tab.brandNew);
    const notificationLabel = computed(() => props.tab.notificationLabel);

    const companyId = useGetCompanyId();
    const toUrl = computed(() => (typeof props.tab.to === 'string' ? props.tab.to : `/${companyId}/${String(props.tab.to.name)}`));
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';

    .list_item {
        list-style: none;
        $i: &;

        &:first-child {
            #{$i}_link {
                border-radius: var(--joy-core-radius-3) var(--joy-core-radius-3) 0 0;
            }
        }
        &:last-child {
            #{$i}_link {
                border-radius: 0 0 var(--joy-core-radius-3) var(--joy-core-radius-3);
            }
        }

        &_link {
            height: 48px;
            width: 100%;
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            @include text.getFontBySize(primary-300);
            text-decoration: none !important;
            background-color: var(--joy-color-background-default);
            color: var(--joy-color-neutral-40);
            transition: background-color 0.2s ease-in;

            &_icon {
                padding: 0 16px;
            }

            &_label {
                flex: 1;
            }

            &_new {
                padding: 0 16px;
            }

            &_notification {
                padding: 0 16px;
                .joy-badge {
                    background: var(--joy-color-primary-30);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-radius: 65px;
                    color: var(--joy-color-neutral-10);
                    text-align: center;
                    font-size: var(--joy-font-size-primary-100);
                }
            }

            &_status {
                padding: 0 16px;
            }

            &.router-link-active {
                background-color: var(--joy-color-secondary-10);
                color: var(--joy-color-secondary-50);
            }

            &:not(.router-link-active):hover {
                background-color: var(--joy-color-secondary-10);
            }
        }
    }
</style>
